import { Select, Space, Text } from '@bfe/moly';
import { message, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useI18nContext } from '@/i18n/I18n';
import { setTimeZone } from '@/services/setting';
import { useStore } from '@/store/useStore';
import { $tracing } from '@/utils/tracing';

const TimeZone = () => {
  const [$t, lang] = useI18nContext();
  const { i18n } = useTranslation(['inverse', 'inverse-error-code']);
  const [localeTimeZone, setLocaleTimeZone] = useState('');
  const [selectTimeZone, setSelectTimeZone] = useState('24');
  const { userInfo, mutateUserInfo } = useStore();

  const timeZones = useMemo(() => {
    const zones = [
      {
        label: $t('settingsTimezone.24h'),
        value: '24',
      },
    ];
    for (let i = 24; i >= 0; i--) {
      let zone = i > 12 ? `+ ${i - 12}` : `- ${12 - i}`;
      if (i === 12) {
        zone = '+ 0';
      }
      // 将当前时区插入时区列表第二个位置
      if (localeTimeZone && zone === localeTimeZone) {
        zones.splice(1, 0, {
          label: `UTC ${localeTimeZone} : 00${$t(
            'settingsTimezone.localeTimeZone',
          )}`,
          value: localeTimeZone.replaceAll(' ', '').replace('+', ''),
        });
      } else {
        zones.push({
          label: `UTC ${zone} : 00`,
          value: zone.replaceAll(' ', '').replace('+', ''),
        });
      }
    }

    return zones;
  }, [localeTimeZone, lang, i18n.isInitialized]);

  const getLocaleTimeZone = () => {
    const now = dayjs();

    // 获取当前时区偏移（以分钟为单位）
    const timeZoneOffsetInMinutes = now.utcOffset();

    // 计算时区偏移的小时和分钟部分
    const hours = Math.abs(Math.floor(timeZoneOffsetInMinutes / 60));

    // 构建时区表示字符串
    const timeZoneString = `${
      timeZoneOffsetInMinutes >= 0 ? '+ ' : '- '
    }${hours}`;

    setLocaleTimeZone(timeZoneString);
  };

  useEffect(() => {
    getLocaleTimeZone();
  }, []);

  useEffect(() => {
    if (
      userInfo?.member_tag?.change_utc_time_zone &&
      timeZones.some(
        (item) => item.value === userInfo?.member_tag?.change_utc_time_zone,
      )
    ) {
      setSelectTimeZone(userInfo?.member_tag?.change_utc_time_zone);
    }
  }, [userInfo?.member_tag?.change_utc_time_zone, timeZones]);

  const changeHandler = (value: any) => {
    const oldTimeZone = selectTimeZone;
    setSelectTimeZone(value);
    setTimeZone({ upsert_keys: { change_utc_time_zone: value } })
      .then(() => {
        message.success({
          content: $t('settingsTimezone.success'),
        });
        console.log('userInfo ====>', {
          ...userInfo,
          member_tag: {
            ...userInfo?.member_tag,
            change_utc_time_zone: value,
          },
        });
        mutateUserInfo((profile: any) => {
          if (profile?.result)
            profile.result = {
              ...userInfo,
              member_tag: {
                ...userInfo?.member_tag,
                change_utc_time_zone: value,
              },
            };
          return profile;
        });
      })
      .catch((error) => {
        setSelectTimeZone(oldTimeZone);
        message.error({
          content: $t('settingsTimezone.fail'),
        });
        $tracing.addEvent({
          t: 'action',
          r: error?.ret_msg || 'Time zone setting failed',
        });
      });
  };

  return (
    <>
      <div className="time-zone">
        <div className="time-zone-label">
          <Text size={14} type="secondary">
            <Space size={4}>
              {$t('settingsTimezone.title')}
              <Tooltip
                title={
                  <ul className="time-zone-tips">
                    <li>{$t('settingsTimezone.tips1')}</li>
                    <li>{$t('settingsTimezone.tips2')}</li>
                  </ul>
                }
                trigger="hover"
                placement="top"
              >
                <i
                  className="moly-iconfont bit-icon_help"
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            </Space>
          </Text>
        </div>
        <div>
          <Select
            width={480}
            value={selectTimeZone}
            size="medium"
            onChange={changeHandler}
            options={timeZones}
          />
        </div>
      </div>
    </>
  );
};

export default TimeZone;
